import mixpanel, { Config, RequestOptions } from "mixpanel-browser";
import { MIXPANEL_RECORD_SESSIONS_PERCENT } from "lib/constants";

interface MixpanelExtendedConfig extends Partial<Config> {
  record_heatmap_data?: boolean;
  record_sessions_percent?: number;
}

export const getMixpanelRecordSessionsPercent = (
  recordSessionsPercent = MIXPANEL_RECORD_SESSIONS_PERCENT,
  date = new Date()
) => {
  if (!isWithinUkOfficeHours(date)) {
    return 0;
  }

  const parsedRecordSessionsPercent = Number(recordSessionsPercent);

  return Number.isFinite(parsedRecordSessionsPercent)
    ? parsedRecordSessionsPercent
    : 20;
};

export const isWithinUkOfficeHours = (date = new Date()) => {
  const londonDateParts = new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    hour12: false,
    timeZone: "Europe/London",
    weekday: "short",
  }).formatToParts(date);

  const weekday = londonDateParts.find(({ type }) => type === "weekday")?.value;
  const hour = Number(
    londonDateParts.find(({ type }) => type === "hour")?.value
  );

  return (
    weekday !== "Sat" &&
    weekday !== "Sun" &&
    Number.isFinite(hour) &&
    hour >= 9 &&
    hour < 17
  );
};

export const syncMixpanelConsent = () => {
  if (typeof window === "undefined") {
    return;
  }

  if (
    window.Cookiebot?.consent?.statistics &&
    !mixpanel.has_opted_in_tracking()
  ) {
    mixpanel.opt_in_tracking();
  }
};

/**
 * TODO: There is no need for this pseudo-singleton init pattern - this
 * has been copied from sifted-shared-utils, and could be refactored
 * to interface with the mixpanel-browser library directly
 */
export class Mixpanel {
  constructor(token: string) {
    const config: MixpanelExtendedConfig = {
      debug: false,
      opt_out_tracking_by_default: true,
      ignore_dnt: true,
      record_heatmap_data: true,
      record_sessions_percent: getMixpanelRecordSessionsPercent(),
      api_host: "https://api-eu.mixpanel.com",
    };
    if (typeof window !== "undefined") {
      mixpanel.init(token, config);
      syncMixpanelConsent();
    }
  }

  track(eventName: string, properties: object, options?: RequestOptions) {
    if (typeof window !== "undefined") {
      mixpanel.track(eventName, properties, options);
    }
  }

  identify(userId: string) {
    if (typeof window !== "undefined") {
      mixpanel.identify(userId);
    }
  }

  peopleSet(data: object) {
    if (typeof window !== "undefined") {
      mixpanel.people.set(data);
    }
  }

  reset() {
    if (typeof window !== "undefined") {
      mixpanel.reset();
    }
  }

  opt_out_tracking() {
    if (typeof window !== "undefined") {
      mixpanel.opt_out_tracking();
    }
  }

  opt_in_tracking() {
    if (typeof window !== "undefined") {
      mixpanel.opt_in_tracking();
    }
  }

  has_opted_in_tracking() {
    if (typeof window !== "undefined") {
      return mixpanel.has_opted_in_tracking();
    }
  }

  has_opted_out_tracking() {
    if (typeof window !== "undefined") {
      return mixpanel.has_opted_out_tracking();
    }
  }
}

export const setMixpanelOnPageLoad = () => {
  if (!mixpanel) return;
  // Collecting this data to debug login/logout issues
  mixpanel.register({
    isLinkedIn: /linkedin/i.test(navigator.userAgent),
    userAgent: navigator.userAgent,
  });
  syncMixpanelConsent();
};

export const setMixpanelOnAccept = () => {
  if (!mixpanel) return;
  if (
    window.Cookiebot?.consent?.statistics &&
    !mixpanel.has_opted_in_tracking()
  ) {
    mixpanel.opt_in_tracking();
  } else if (!mixpanel.has_opted_out_tracking) {
    mixpanel.opt_out_tracking();
  }
};

export const setMixpanelOnDecline = () => {
  if (!mixpanel) return;
  if (
    !window.Cookiebot?.consent?.statistics &&
    !mixpanel.has_opted_out_tracking()
  ) {
    mixpanel.opt_out_tracking();
  }
};
